import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Download, Info, Users } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Spinner } from '../../lib/ui.js';
import { isNativeApp } from '../../lib/native.js';
import { t } from '../../i18n/index.js';
import type { VersionInfo } from '../../lib/app-version.js';
import { detectPlatform } from './AddToHomePage.js';

/**
 * Where an employee gets the app, and the last step of joining.
 *
 * Public on purpose. Somebody who lost their phone, or wiped it, needs this page before they can
 * sign in to anything, so the address the admin sends is the same one whatever has happened —
 * there is no per-person link to reissue.
 *
 * Three routes, chosen by the owner in Settings and decided by the server:
 *
 *  - **Play, public listing.** One tap. What this becomes once the app has production access.
 *  - **Play, testing track.** Join the employee group, then install. In that order, because
 *    Play enforces it: its own refusal page says to make sure you have joined the Group. This
 *    was briefly one button to the opt-in page with the group as a footnote, built on a single
 *    successful test from a single Google account; the owner's phone then met 'App not
 *    available' and said otherwise. Nobody is added to a list when they are hired, the install
 *    carries no 'unsafe to install' warning, and Play updates every phone silently from then on.
 *
 * There is deliberately no direct download here. The APK carries an "unsafe to install" warning
 * and installs nothing that updates itself, which is what Play was adopted to be rid of; the
 * page at /android still exists for the sideloaded copies already on phones, and nothing sends
 * an employee to it.
 *
 * An iPhone gets none of them, because there is no iOS app; it gets the home-screen step.
 */
/**
 * Whether this phone has been sent to the group page.
 *
 * Not whether it joined: Google offers no way to ask, and this page is public so there is no
 * Google account on our side to ask about. It is the difference between guiding somebody and
 * claiming to have checked, and only one of those is true here.
 */
const GROUP_KEY = 'siva.playGroupOpened';

function groupOpened(): boolean {
  try {
    return localStorage.getItem(GROUP_KEY) === '1';
  } catch {
    return false;
  }
}

function markGroupOpened(): void {
  try {
    localStorage.setItem(GROUP_KEY, '1');
  } catch {
    /* private mode: the step simply asks again, which costs a tap and misleads nobody */
  }
}

export function GetTheAppPage() {
  const navigate = useNavigate();
  /** Step 2 opens once step 1 has been tapped, or once they say they joined already. */
  const [joined, setJoined] = useState(groupOpened);
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const [failed, setFailed] = useState(false);
  const platform = detectPlatform();

  /**
   * Already inside the app: this page would be asking somebody to install what they are reading
   * it in. Being installed to the home screen does not count — on Android that is the web app,
   * not the one from Play, and those are exactly the people still worth offering it to.
   */
  useEffect(() => {
    if (isNativeApp()) navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    api<VersionInfo>('/android/version')
      .then(setInfo)
      .catch(() => setFailed(true));
  }, []);

  // An iPhone has no app to install. Send them where the instructions actually apply.
  useEffect(() => {
    if (platform === 'ios') navigate('/add-to-home', { replace: true });
  }, [platform, navigate]);

  const onward = () => navigate('/', { replace: true });

  /**
   * "Done, take me in" is not offered on an Android phone: the app is what that person is here
   * to install, and a button inviting them to carry on punching in the browser instead is an
   * invitation to never install it. It stays for a desktop, where there is nothing to install
   * and no other way forward.
   *
   * This is a nudge and not a gate. Somebody who reopens the app's address is signed in and at
   * the punch screen as before; refusing a browser punch outright is the android_app_required
   * setting's job, and it binds once a worker has punched from the app at least once.
   */
  const offerSkip = platform !== 'android';

  if (platform === 'ios') return null;
  if (!info && !failed) return <Spinner />;

  const play = info?.play ?? null;

  return (
    <section className="card get-app" aria-label={t('app_get_title')}>
      <h2>{t('app_get_title')}</h2>
      <p>{t('app_get_why')}</p>

      {play?.route === 'public' ? (
        <>
          <p>
            <a className="button big" href={play.store_url} target="_blank" rel="noreferrer">
              <Download size={16} aria-hidden="true" /> {t('app_get_install')}
            </a>
          </p>
          <p className="muted">{t('app_get_public_help')}</p>
        </>
      ) : play?.route === 'group' ? (
        <>
          {/*
            Two steps, in the order Play enforces. Google's own refusal page says it outright —
            "If you've been invited to a Google Group as part of the program, make sure you've
            joined the Group" — so joining is not a fallback for when something goes wrong, it is
            what makes the second step work at all. An employee who taps Install first is told the
            app is not available for their account, which reads like the company's mistake.

            Both open in a new tab, so this page is still here when they come back to it.
          */}
          <ol className="get-app-steps">
            {play.group_url && (
              <li>
                <span className="step-what">{t('app_get_step_group')}</span>
                <a
                  className="button small"
                  href={play.group_url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    markGroupOpened();
                    setJoined(true);
                  }}
                >
                  <Users size={15} aria-hidden="true" /> {t('app_get_join_group')}
                </a>
                <span className="muted block-help">{t('app_get_step_group_help')}</span>
              </li>
            )}
            <li className={play.group_url && !joined ? 'step-waiting' : undefined}>
              <span className="step-what">{t('app_get_step_install')}</span>
              {play.group_url && !joined ? (
                <>
                  {/*
                    A disabled button rather than a hidden one: the worker can see what is coming
                    and why it is not available yet, instead of the page seeming to have one step.
                  */}
                  <button type="button" className="small" disabled>
                    <Download size={15} aria-hidden="true" /> {t('app_get_install')}
                  </button>
                  <span className="muted block-help">{t('app_get_install_locked')}</span>
                  <button
                    type="button"
                    className="link small"
                    onClick={() => {
                      markGroupOpened();
                      setJoined(true);
                    }}
                  >
                    {t('app_get_already_joined')}
                  </button>
                </>
              ) : (
                <>
                  <a
                    className="button small"
                    href={play.opt_in_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Download size={15} aria-hidden="true" /> {t('app_get_install')}
                  </a>
                  <span className="muted block-help">{t('app_get_optin_help')}</span>
                </>
              )}
            </li>
          </ol>

          {/*
            The two things that make somebody think it is broken when it is not: the Play Store
            does not list a test build in search at all, and the first time an account is added
            it can be hours before Play serves it. Both are Google's, neither is an error, and
            saying so here is cheaper than the phone call.
          */}
          <p className="app-get-note">
            <Info size={15} aria-hidden="true" />
            <span>{t('app_get_wait')}</span>
          </p>

        </>
      ) : (
        /*
         * Play is the only way in, and it is not set up yet. Say so plainly rather than offering
         * an APK: an employee cannot fix this and should not be asked to work around it.
         */
        <p className="muted">{t('app_get_not_ready')}</p>
      )}

      {offerSkip && (
        <div className="row">
          <button type="button" className="secondary" onClick={onward}>
            <Check size={16} aria-hidden="true" /> {t('app_get_done')}
          </button>
        </div>
      )}
    </section>
  );
}
